import { CASE_STATE_TONE, type CaseState, fmtFull, fmtMonthDayYear, zoneLabel } from '@concord/domain';
import type { EntitlementView } from '@concord/domain/contracts';
import {
  Banner,
  Button,
  ConfirmDialog,
  Crumbs,
  EntitlementChain,
  KeyValue,
  Notice,
  PageHead,
  Panel,
  PanelHead,
  PhaseTag,
  Pill,
  RevealValue,
  useToast,
} from '@concord/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import type { Me } from '../../lib/session';
import { ConsoleLayout } from '../console/ConsoleLayout';
import { EditAccountDialog, PermissionsPanel } from './AccountEditor';
import { AdminAccess } from './AdminAccess';
import { HandoverSelect, type OwnedCase, OwnedCasesPanel, useOtherNavigators } from './NavigatorCases';
import { PlanMembership } from './PlanMembership';

interface Account {
  id: string;
  publicId: number;
  role: string;
  roleLabel: string;
  firstName: string;
  lastName: string;
  displayName: string;
  email: string;
  phone: string | null;
  mobile: string | null;
  mobileVerified: boolean;
  timezone: string;
  country: string;
  city: string | null;
  enabled: boolean;
  registrationState: 'invited' | 'registered';
  isTest: boolean;
  isSupport: boolean;
  doNotDisturb: boolean;
  notificationPreference: string;
  referralSource: string | null;
  lastLoginAt: string | null;
  lastLoginLocation: string | null;
  lastInvalidLoginAt: string | null;
  invalidLoginCount: number;
  reactivatedAt: string | null;
  deactivatedAt: string | null;
  createdAt: string;
  activeSessions: number;
  authenticatorEnabledAt: string | null;
  permissions: string[];
  plan: { name: string; customer: string; validTo: string | null } | null;
  entitlement: EntitlementView | null;
  invitations: {
    id: string;
    state: string;
    issuedAt: string;
    expiresAt: string;
    acceptedAt: string | null;
    suppressEmail: boolean;
  }[];
  history: { at: string; type: string; by: string | null }[];
  cases: {
    id: string;
    reference: string | null;
    state: CaseState;
    stateLabel: string;
    submittedAt: string | null;
  }[];
  ownedOpenCases: OwnedCase[];
}

/** The account record on one screen, as the navigator reads it aloud (DIR-05 to DIR-09). */
export function AccountPage({ me }: { me: Me }) {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [confirm, setConfirm] = useState(false);
  const [editing, setEditing] = useState(false);
  const superAdmin = me.permissions.includes('admin.super');
  const [handTo, setHandTo] = useState('');
  const { others } = useOtherNavigators(id);
  const q = useQuery({
    queryKey: ['account', id],
    queryFn: () => api<Account>(`/staff/directory/account/${id}`),
  });
  const done = (msg: string) => async () => {
    await qc.invalidateQueries({ queryKey: ['account', id] });
    toast(msg);
  };
  const update = useMutation({
    mutationFn: (f: Record<string, boolean | string>) =>
      api(`/staff/directory/account/${id}`, { method: 'PATCH', body: f }),
    onSuccess: done('Account updated.'),
    onError: (e) => toast(errorMessage(e), 'bad'),
  });
  const reissue = useMutation({
    mutationFn: () => api<{ link: string }>(`/staff/accounts/${id}/invitation/reissue`, { body: {} }),
    onSuccess: async (r) => {
      await navigator.clipboard.writeText(r.link).catch(() => undefined);
      await done('New link issued and copied. The old link no longer works.')();
    },
    onError: (e) => toast(errorMessage(e), 'bad'),
  });
  const resetApp = useMutation({
    mutationFn: () => api(`/staff/directory/account/${id}/authenticator/reset`, { body: {} }),
    onSuccess: done(
      'Authenticator app removed. They sign in with an emailed code until they set up a new one.',
    ),
    onError: (e) => toast(errorMessage(e), 'bad'),
  });
  const reveal = async (field: string) =>
    (await api<{ value: string | null }>(`/staff/directory/account/${id}/reveal?field=${field}`)).value;
  const tz = me.timezone;
  const a = q.data;
  // Whoever takes a disabled navigator's open cases: the chosen one, else me, else the first other.
  const handover =
    a && a.ownedOpenCases.length > 0
      ? handTo || (others.some((n) => n.id === me.accountId) ? me.accountId : (others[0]?.id ?? ''))
      : '';
  const d = (iso: string | null) => (iso ? fmtFull(new Date(iso), tz) : 'Never');
  const yes = (b: boolean) => (b ? 'Yes' : 'No');

  return (
    <ConsoleLayout me={me} current="directory">
      {q.isError && <Notice tone="bad">{errorMessage(q.error)}</Notice>}
      {a && (
        <>
          <Crumbs items={[{ label: 'Directory', to: '/console/directory' }, { label: `ID ${a.publicId}` }]} />
          <PageHead
            title={
              <>
                {a.firstName} {a.lastName} <Pill tone={a.enabled ? 'acc' : 'bad'}>{a.roleLabel}</Pill>
                {a.registrationState === 'invited' && <Pill tone="hold">Invited, not registered</Pill>}
                {!a.enabled && <Pill tone="bad">Disabled</Pill>}
                {a.isTest && <Pill tone="idle">Test</Pill>}
              </>
            }
            when={`${a.email} · ID ${a.publicId} · ${zoneLabel(a.timezone)}`}
            actions={
              <>
                {superAdmin && (
                  <Button variant="sec" onClick={() => setEditing(true)}>
                    Edit account
                  </Button>
                )}
                {a.registrationState === 'invited' && (
                  <Button variant="sec" busy={reissue.isPending} onClick={() => reissue.mutate()}>
                    Reissue invitation
                  </Button>
                )}
                {a.role !== 'expert' && a.id !== me.accountId && (
                  <Button variant={a.enabled ? 'danger' : 'sec'} onClick={() => setConfirm(true)}>
                    {a.enabled ? 'Disable account' : 'Enable account'}
                  </Button>
                )}
              </>
            }
          />
          {a.entitlement && (
            <>
              <EntitlementChain
                links={a.entitlement.links}
                note={a.entitlement.valid ? 'Chain valid' : a.entitlement.message}
              />
              {!a.entitlement.valid && (
                <Banner
                  tone="bad"
                  title="Entitlement broken"
                  detail={`${a.entitlement.message} A new case cannot start.`}
                />
              )}
            </>
          )}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '1.25rem',
              alignItems: 'start',
            }}
          >
            <Panel aria-label="Account">
              <PanelHead title="Account" sub="Overview" />
              <div className="pbody">
                <KeyValue
                  rows={[
                    ['Role', a.roleLabel],
                    ['Display name', a.displayName],
                    ['Email', a.email],
                    ['Account ID', String(a.publicId)],
                    ['Status', a.enabled ? 'Enabled' : 'Disabled'],
                    [
                      'Registration',
                      a.registrationState === 'registered' ? 'Registered' : 'Invited, not registered',
                    ],
                    ['Local time zone', zoneLabel(a.timezone)],
                    [
                      'Phone',
                      <RevealValue key="p" masked={a.phone} label="phone" reveal={() => reveal('phone')} />,
                    ],
                    [
                      'Mobile',
                      a.mobile ? (
                        <span key="m">
                          <RevealValue masked={a.mobile} label="mobile" reveal={() => reveal('mobile')} />
                          {a.mobileVerified ? ' · verified' : ' · not verified'}
                        </span>
                      ) : (
                        '—'
                      ),
                    ],
                    [
                      'City',
                      <RevealValue key="c" masked={a.city} label="city" reveal={() => reveal('city')} />,
                    ],
                    ['Notifications', a.notificationPreference],
                    ['Do not disturb', yes(a.doNotDisturb)],
                    ['Test account', yes(a.isTest)],
                    ['Support account', yes(a.isSupport)],
                    ['Referral source', a.referralSource ?? '—'],
                    ['Country', a.country],
                  ]}
                />
              </div>
            </Panel>
            <Panel aria-label="Security">
              <PanelHead title="Sign-in and security" />
              <div className="pbody">
                <KeyValue
                  rows={[
                    ['Last login', d(a.lastLoginAt)],
                    [
                      'Last location',
                      <RevealValue
                        key="l"
                        masked={a.lastLoginLocation}
                        label="last sign-in location"
                        reveal={() => reveal('lastLoginLocation')}
                      />,
                    ],
                    ['Last invalid login', d(a.lastInvalidLoginAt)],
                    ['Invalid logins', String(a.invalidLoginCount)],
                    ['Active sessions', String(a.activeSessions)],
                    ...(a.role === 'navigator'
                      ? ([
                          [
                            'Authenticator app',
                            <span
                              key="totp"
                              style={{ display: 'inline-flex', gap: '.5rem', alignItems: 'center' }}
                            >
                              {a.authenticatorEnabledAt
                                ? `On since ${d(a.authenticatorEnabledAt)}`
                                : 'Not set up'}
                              {a.authenticatorEnabledAt &&
                                (superAdmin || me.permissions.includes('admin.audit')) &&
                                a.id !== me.accountId && (
                                  <Button
                                    variant="ghost"
                                    busy={resetApp.isPending}
                                    onClick={() => resetApp.mutate()}
                                  >
                                    Remove
                                  </Button>
                                )}
                            </span>,
                          ],
                        ] as [string, ReactNode][])
                      : []),
                    ['Reactivated', a.reactivatedAt ? d(a.reactivatedAt) : '—'],
                    ['Deactivated', a.deactivatedAt ? d(a.deactivatedAt) : '—'],
                    ['Created', d(a.createdAt)],
                    ['Permissions', a.permissions.length ? a.permissions.join(', ') : 'None'],
                  ]}
                />
              </div>
            </Panel>
            {a.role === 'patient' ? (
              <PlanMembership
                accountId={a.id}
                name={a.firstName}
                tz={tz}
                canManage={me.permissions.includes('accounts.manage')}
              />
            ) : (
              <Panel aria-label="Plan">
                <PanelHead title="Plan membership" />
                <div className="pbody">
                  {a.plan ? (
                    <KeyValue
                      rows={[
                        ['Plan', `${a.plan.customer} › ${a.plan.name}`],
                        [
                          'Membership ends',
                          a.plan.validTo ? fmtMonthDayYear(new Date(a.plan.validTo), tz) : 'Open-ended',
                        ],
                      ]}
                    />
                  ) : (
                    <span className="sub2">No plan.</span>
                  )}
                </div>
              </Panel>
            )}
            <Panel aria-label="Cases">
              <PanelHead title="Cases" sub={a.cases.length} />
              <div className="pbody" style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
                {a.cases.length === 0 && <span className="sub2">No cases.</span>}
                {a.cases.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className="link"
                    style={{ textAlign: 'left', display: 'flex', gap: '.5rem', alignItems: 'center' }}
                    onClick={() => c.reference && navigate(`/console/cases/${c.reference}`)}
                  >
                    <span className="ref">{c.reference ?? 'Draft'}</span>{' '}
                    <Pill tone={CASE_STATE_TONE[c.state]}>{c.stateLabel}</Pill>
                  </button>
                ))}
              </div>
            </Panel>
            <Panel aria-label="Invitation history">
              <PanelHead title="Invitations" sub={a.invitations.length} />
              <div className="pbody">
                <KeyValue
                  rows={a.invitations.map((i) => [
                    d(i.issuedAt),
                    `${i.state}${i.acceptedAt ? `, accepted ${d(i.acceptedAt)}` : ''} · expires ${d(i.expiresAt)}${i.suppressEmail ? ' · not emailed' : ''}`,
                  ])}
                />
              </div>
            </Panel>
            {a.role === 'navigator' && superAdmin && (
              <PermissionsPanel accountId={a.id} self={a.id === me.accountId} />
            )}
            {a.role === 'navigator' && !superAdmin && me.permissions.includes('admin.audit') && (
              <AdminAccess
                accountId={a.id}
                isAdmin={a.permissions.includes('admin.audit')}
                self={a.id === me.accountId}
              />
            )}
            {a.role === 'navigator' && (
              <OwnedCasesPanel
                accountId={a.id}
                name={a.firstName}
                cases={a.ownedOpenCases}
                defaultTo={me.accountId}
              />
            )}
            {a.role === 'expert' && (
              <Panel aria-label="Physician">
                <PanelHead title="Physician profile">
                  <PhaseTag phase="P5" />
                </PanelHead>
                <div className="pbody sub2">
                  Earnings (Phase 3), specialties, disclosures and unavailability (Phase 5) are managed here
                  in later phases.
                </div>
              </Panel>
            )}
          </div>
          {/* Full width and last: the history grows with every change. */}
          <Panel aria-label="Account history" style={{ marginTop: '1.25rem' }}>
            <PanelHead title="History" sub="Every change, newest first" />
            <div className="pbody">
              <KeyValue
                rows={a.history.map((h) => [
                  d(h.at),
                  `${h.type.replace(/[._]/g, ' ')}${h.by ? ` · ${h.by}` : ''}`,
                ])}
              />
            </div>
          </Panel>
          <ConfirmDialog
            open={confirm}
            onOpenChange={setConfirm}
            title={
              a.enabled ? `Disable ${a.firstName} ${a.lastName}?` : `Enable ${a.firstName} ${a.lastName}?`
            }
            consequence={
              a.enabled
                ? 'They are signed out everywhere at once and cannot sign in until the account is enabled again. Both dates are kept. This can be undone.'
                : 'They can sign in again with their email address.'
            }
            confirmLabel={a.enabled ? 'Disable' : 'Enable'}
            danger={a.enabled}
            busy={update.isPending}
            onConfirm={() =>
              update.mutate(
                a.enabled && handover ? { enabled: false, reassignTo: handover } : { enabled: !a.enabled },
                { onSettled: () => setConfirm(false) },
              )
            }
          >
            {a.enabled && a.ownedOpenCases.length > 0 && (
              <HandoverSelect
                count={a.ownedOpenCases.length}
                options={others}
                value={handover}
                onChange={setHandTo}
              />
            )}
          </ConfirmDialog>
        </>
      )}
      {a && superAdmin && (
        <EditAccountDialog
          accountId={a.id}
          self={a.id === me.accountId}
          open={editing}
          onOpenChange={setEditing}
        />
      )}
    </ConsoleLayout>
  );
}
